import React, { useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, TextField, MenuItem, Alert, Button,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useMovimientoStore } from '../../movimientos/store/movimientoStore';
import { useCajaBancoStore } from '../../cajas-bancos/store/cajaBancoStore';

const ExploradorTesoreriaPage: React.FC = () => {
  const {
    movimientos, loading, error, filtro,
    fetchMovimientos, setFiltro, clearError,
  } = useMovimientoStore();
  const { cajasBancos, fetchCajasBancos } = useCajaBancoStore();

  useEffect(() => {
    fetchMovimientos();
    fetchCajasBancos();
  }, [fetchMovimientos, fetchCajasBancos]);

  const columns: GridColDef[] = [
    { field: 'numero', headerName: 'Numero', width: 130 },
    {
      field: 'fechaMovimiento',
      headerName: 'Fecha',
      width: 110,
      valueFormatter: (value: string) =>
        value ? new Date(value).toLocaleDateString('es-PE') : '',
    },
    {
      field: 'tipoMovimiento',
      headerName: 'Tipo',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'INGRESO' ? 'success' : 'error'}
        />
      ),
    },
    { field: 'cajaBancoNombre', headerName: 'Caja/Banco', width: 150 },
    { field: 'nombreAuxiliar', headerName: 'Auxiliar', flex: 1, minWidth: 150 },
    { field: 'tipoOperacion', headerName: 'Tipo Oper.', width: 120 },
    { field: 'numeroOperacion', headerName: 'Nro. Oper.', width: 120 },
    {
      field: 'montoTotal',
      headerName: 'Monto',
      width: 130,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
    { field: 'monedaSimbolo', headerName: 'Mon.', width: 60 },
    { field: 'glosa', headerName: 'Glosa', flex: 1, minWidth: 200 },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'ACTIVO' ? 'success' : 'default'}
          variant={params.value === 'ANULADO' ? 'outlined' : 'filled'}
        />
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        Explorador de Tesoreria
      </Typography>

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Fecha Inicio"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtro.fechaInicio || ''}
            onChange={(e) => setFiltro({ fechaInicio: e.target.value })}
          />
          <TextField
            label="Fecha Fin"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtro.fechaFin || ''}
            onChange={(e) => setFiltro({ fechaFin: e.target.value })}
          />
          <TextField
            label="Caja/Banco"
            select
            size="small"
            sx={{ minWidth: 150 }}
            value={filtro.idCajaBanco || ''}
            onChange={(e) =>
              setFiltro({ idCajaBanco: e.target.value ? parseInt(e.target.value) : undefined })
            }
          >
            <MenuItem value="">Todos</MenuItem>
            {cajasBancos.map((cb) => (
              <MenuItem key={cb.idCajaBanco} value={cb.idCajaBanco}>
                {cb.nombre}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchMovimientos}>
            Consultar
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ height: 'calc(100vh - 280px)' }}>
        <DataGrid
          rows={movimientos}
          columns={columns}
          getRowId={(row) => row.idMovimientoTesoreria}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Paper>
    </Box>
  );
};

export default ExploradorTesoreriaPage;
